// page-contact.jsx — お問い合わせ（CF7 駆動・タイプ切替対応・2階層タブ）
//
// PHP 側 page-contact.php が、Contact Form 7 のフォーム HTML を
// 種別ごとに do_shortcode() で生成し window.HB_CONTACT に注入する。
//
// データ構造（2階層対応）:
// {
//   forms: {
//     general:    { label, desc, html },
//     partnership:{ label, desc, html },
//     document:   {                              ← グループタブ
//       label, desc,
//       children: {
//         'document-company':  { label, desc, html },
//         'document-training': { label, desc, html },
//         'document-apply':    { label, desc, html, note },
//       },
//       order: ['document-company', 'document-training', 'document-apply'],
//     },
//     'tax-office':{ label, desc, html },
//     recruit:    { label, desc, html },
//     'ax-dx':    { label, desc, html, note },
//   },
//   initial: 'general',
//   order:   ['general','partnership','document','tax-office','recruit','ax-dx']  ← top-level
// }
//
// このコンポーネントは ?type= URL パラメータ／タブ切替で表示する CF7 を切り替え、
// CF7 の HTML を dangerouslySetInnerHTML でマウントする。
// CF7 の JS（form 送信ハンドラ・バリデーション）は wp_footer 経由で別途ロード済み。

(function(){
  const { HBLayout, HBSubHero, hbcp, fontEN, fontENSans, fontJP, fontMono, HB_BASE } = window;

  /**
   * active key → top-level group key を解決する。
   * 例: 'document-company' → 'document', 'general' → 'general'
   */
  function resolveGroupKey( activeKey, forms ) {
    // 直接 top-level に存在する単独フォーム
    if ( forms[ activeKey ] && ! forms[ activeKey ].children ) return activeKey;
    if ( forms[ activeKey ] && forms[ activeKey ].children ) return activeKey;
    // グループ内の子キーを探す
    for ( const gKey of Object.keys( forms ) ) {
      const g = forms[ gKey ];
      if ( g.children && g.children[ activeKey ] ) return gKey;
    }
    return activeKey;
  }

  /**
   * active key からレンダリング対象のフォームデータを取得する。
   * グループキーが渡された場合は、最初の子フォームを返す。
   */
  function resolveFormData( activeKey, forms ) {
    const direct = forms[ activeKey ];
    if ( direct && ! direct.children ) return direct;
    if ( direct && direct.children ) {
      const firstChild = direct.order && direct.order[0];
      return firstChild ? direct.children[ firstChild ] : null;
    }
    for ( const gKey of Object.keys( forms ) ) {
      const g = forms[ gKey ];
      if ( g.children && g.children[ activeKey ] ) return g.children[ activeKey ];
    }
    return null;
  }

  function ContactPage() {
    const data = window.HB_CONTACT || { forms:{}, initial:'general', order:['general'] };
    const { forms, initial, order } = data;

    // 全有効キー（top-level + child keys）— URL ?type= validate 用
    const allValidKeys = React.useMemo( () => {
      const keys = [];
      Object.keys( forms ).forEach( k => {
        keys.push( k );
        if ( forms[ k ].children ) {
          Object.keys( forms[ k ].children ).forEach( ck => keys.push( ck ) );
        }
      });
      return keys;
    }, [ forms ] );

    // 初期タイプ: URL の ?type= を優先（直接リンクからのアクセス対応）
    const urlType = ( () => {
      try {
        const u = new URL( window.location.href );
        return u.searchParams.get( 'type' );
      } catch ( e ) { return null; }
    })();
    const validInitial = ( urlType && allValidKeys.includes( urlType ) ) ? urlType : initial;

    // active が group key の場合は最初の child key に正規化
    const normalizeActive = ( key ) => {
      const f = forms[ key ];
      if ( f && f.children ) {
        return ( f.order && f.order[0] ) || key;
      }
      return key;
    };

    const [ active, setActive ] = React.useState( normalizeActive( validInitial ) );

    const formHtmlRef = React.useRef( null );

    // タブ切替時に履歴を書き換える（共有 URL 対応・リロードしても復元）
    React.useEffect( () => {
      try {
        const u = new URL( window.location.href );
        if ( active && active !== 'general' ) u.searchParams.set( 'type', active );
        else u.searchParams.delete( 'type' );
        window.history.replaceState( null, '', u.toString() );
      } catch ( e ) {}
    }, [ active ] );

    // CF7 の JS は wp_footer で全フォームを自動 init するが、innerHTML で差し替えると
    // 再 init が必要。グローバルな wpcf7.init があれば呼ぶ。
    React.useEffect( () => {
      if ( ! formHtmlRef.current ) return;
      // CF7 v5+ は wpcf7.init( formEl ) で個別 init を要求する
      const formEl = formHtmlRef.current.querySelector( 'form.wpcf7-form' );
      if ( formEl && window.wpcf7 && typeof window.wpcf7.init === 'function' ) {
        try { window.wpcf7.init( formEl ); } catch ( e ) {}
      }
    }, [ active ] );

    const activeGroupKey = resolveGroupKey( active, forms );
    const activeGroup = forms[ activeGroupKey ] || null;
    const hasSubTabs = !! ( activeGroup && activeGroup.children );
    const current = resolveFormData( active, forms ) || { label:'', desc:'', html:'<p>フォームの準備中です。</p>', note:'' };

    // ───────────────────────────────────────────
    // スクロール可能タブ列の「もっと右にある」感を演出するヘルパ
    // Apple App Store / M3 ハイブリッド：peek + 両端 fade + snap
    // ───────────────────────────────────────────
    const useScrollFade = () => {
      const ref = React.useRef( null );
      const [ left,  setLeft  ] = React.useState( false );
      const [ right, setRight ] = React.useState( false );
      React.useEffect( () => {
        const el = ref.current; if ( ! el ) return;
        const update = () => {
          setLeft(  el.scrollLeft > 4 );
          setRight( el.scrollLeft + el.clientWidth < el.scrollWidth - 4 );
        };
        update();
        el.addEventListener( 'scroll', update, { passive:true } );
        window.addEventListener( 'resize', update );
        return () => {
          el.removeEventListener( 'scroll', update );
          window.removeEventListener( 'resize', update );
        };
      }, [ activeGroupKey, hasSubTabs ] );
      return [ ref, left, right ];
    };
    const [ mainRef, mainLeft,  mainRight  ] = useScrollFade();
    const [ subRef,  subLeft,   subRight   ] = useScrollFade();

    return (
      <HBLayout>
        <HBSubHero
          crumb={'TOP / お問い合わせ'}
          en="Contact"
          jp={<span><span style={{color:hbcp.blue}}>お問い合わせ</span></span>}
          tag={"Let's translate your questions into actions."}
          lead={'目的別のフォームをご用意しています。お電話・メールでも受け付けています。専門領域に応じて担当チームから 2 営業日以内に返信します。'}
        />

        {/* メインタブ：種別切替（モバイル横スクロール時は両端 fade + peek + snap） */}
        <div style={{borderBottom:`1px solid ${hbcp.line}`, background:hbcp.white, position:'sticky', top:72, zIndex:30}}>
          <div style={{maxWidth:1280, margin:'0 auto', padding:'0 16px', position:'relative'}}>
            <div ref={mainRef} className="hb-tab-scroll" style={{
              display:'flex', overflowX:'auto', gap:0,
              paddingRight:40,                       /* peek next tab */
              scrollSnapType:'x mandatory',
              scrollbarWidth:'thin',
              WebkitOverflowScrolling:'touch',
            }}>
              {order.map( key => {
                const f = forms[ key ];
                if ( ! f ) return null;
                const isActive = key === activeGroupKey;
                return (
                  <button key={key} onClick={() => setActive( normalizeActive( key ) )}
                    style={{
                      background:'none', border:'none', padding:'18px 22px', cursor:'pointer',
                      fontFamily:fontJP, fontSize:13, fontWeight: isActive ? 700 : 500,
                      color: isActive ? hbcp.blue : hbcp.ink,
                      borderBottom: isActive ? `2px solid ${hbcp.blue}` : `2px solid transparent`,
                      whiteSpace:'nowrap', minWidth:'fit-content',
                      scrollSnapAlign:'start',
                    }}>
                    {f.label}
                  </button>
                );
              })}
            </div>
            {/* 両端フェード（スクロール位置に応じて表示） */}
            <div style={{position:'absolute', top:0, bottom:0, left:0, width:32,
              background:`linear-gradient(to right, ${hbcp.white}, transparent)`,
              pointerEvents:'none', opacity: mainLeft ? 1 : 0, transition:'opacity .2s'}}/>
            <div style={{position:'absolute', top:0, bottom:0, right:0, width:40,
              background:`linear-gradient(to left, ${hbcp.white}, transparent)`,
              pointerEvents:'none', opacity: mainRight ? 1 : 0, transition:'opacity .2s'}}/>
          </div>
        </div>

        {/* サブタブ：グループ内切替（資料請求などのみ表示） */}
        {hasSubTabs && (
          <div style={{borderBottom:`1px solid ${hbcp.line}`, background:hbcp.paper, position:'sticky', top:121, zIndex:29}}>
            <div style={{maxWidth:1280, margin:'0 auto', padding:'0 16px', position:'relative'}}>
              <div ref={subRef} className="hb-tab-scroll" style={{
                display:'flex', overflowX:'auto', gap:0,
                paddingRight:40,
                scrollSnapType:'x mandatory',
                scrollbarWidth:'thin',
                WebkitOverflowScrolling:'touch',
              }}>
                {activeGroup.order.map( ckey => {
                  const cf = activeGroup.children[ ckey ];
                  if ( ! cf ) return null;
                  const isActive = ckey === active;
                  return (
                    <button key={ckey} onClick={() => setActive( ckey )}
                      style={{
                        background:'none', border:'none', padding:'14px 20px', cursor:'pointer',
                        fontFamily:fontJP, fontSize:12.5, fontWeight: isActive ? 700 : 500,
                        color: isActive ? hbcp.blue : hbcp.inkSoft,
                        borderBottom: isActive ? `2px solid ${hbcp.blue}` : `2px solid transparent`,
                        whiteSpace:'nowrap', minWidth:'fit-content',
                        scrollSnapAlign:'start',
                      }}>
                      {cf.label}
                    </button>
                  );
                })}
              </div>
              <div style={{position:'absolute', top:0, bottom:0, left:0, width:32,
                background:`linear-gradient(to right, ${hbcp.paper}, transparent)`,
                pointerEvents:'none', opacity: subLeft ? 1 : 0, transition:'opacity .2s'}}/>
              <div style={{position:'absolute', top:0, bottom:0, right:0, width:40,
                background:`linear-gradient(to left, ${hbcp.paper}, transparent)`,
                pointerEvents:'none', opacity: subRight ? 1 : 0, transition:'opacity .2s'}}/>
            </div>
          </div>
        )}

        <section className="hb-contact-section" style={{padding:'56px 32px 96px', background:hbcp.white}}>
          <div className="hb-contact-grid" style={{maxWidth:1280, margin:'0 auto', display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:64, alignItems:'flex-start'}}>

            {/* ===== Form (CF7 HTML を差し込み) ===== */}
            <div>
              <div style={{marginBottom:24}}>
                <h2 style={{fontFamily:fontJP, fontWeight:800, fontSize:22, lineHeight:1.55, margin:'0 0 8px', letterSpacing:'.04em'}}>{current.label}</h2>
                <p style={{fontSize:13.5, lineHeight:1.85, color:hbcp.inkSoft, margin:0}}>{current.desc}</p>
                {current.note && (
                  <p style={{marginTop:12, padding:'10px 14px', background:hbcp.blueGhost, borderLeft:`3px solid ${hbcp.blue}`, fontSize:12.5, lineHeight:1.7, color:hbcp.ink}}>
                    {current.note}
                  </p>
                )}
              </div>
              <div ref={formHtmlRef} className="hb-cf7-host"
                dangerouslySetInnerHTML={{ __html: current.html }}/>

              {/* ===== 個人情報保護方針（全フォーム共通レンダリング） =====
                  CF7 一般フォームのみが本文HTMLに privacy 領域を含んでいたため、
                  partnership / document / tax-office / recruit / ax-dx でも
                  同一内容を表示するためJSX側で常時レンダリングする。
                  CF7 既存のインライン版は .hb-cf7-host .contact__privacy で
                  display:none に隠して重複を防ぐ。 */}
              <section className="hb-contact-privacy-panel" aria-labelledby="hb-privacy-h">
                <div className="hb-contact-privacy-panel__header">
                  <div className="hb-contact-privacy-panel__kicker">● PRIVACY POLICY</div>
                  <h3 id="hb-privacy-h" className="hb-contact-privacy-panel__title">個人情報保護方針</h3>
                </div>
                <div className="hb-contact-privacy-panel__body">
                  <p>はてなベース株式会社（以下、「当社」という。）は、クラウドサービスの選定、業務設計、導入、運用までをワンストップで支援するサービスを提供する事、および、研修動画の作成・販売事業及びこれに付随する経営支援を行っております。当社は、同業務を実施する上で、個人情報の保護が重要な事項であると認識しております。そこで当社は、当社の事業の用に供するすべての個人情報を適切に取扱うため、当社全従業者が遵守すべき行動基準として本個人情報保護方針を定め、その遵守の徹底を図ることといたします。</p>
                  <ol>
                    <li>当法人は、個人情報の保護に関する法令、国が定める指針その他の規範を遵守し、個人情報の保護に万全を尽くします。</li>
                    <li>当社は、事業の内容及び規模を考慮した適切な個人情報の取得、利用及び提供を行います。それには特定された利用目的の達成に必要な範囲を超えた個人情報の取扱いを行わないこと及びそのための措置を講じることを含みます。</li>
                    <li>当社は、個人情報の取扱いの全部又は一部を委託する場合は、その取扱いを委託された個人情報の安全管理が図られるよう、委託を受けた者に対する必要かつ適切な監督を行います。</li>
                    <li>ご本人の事前の同意を得た場合及び、個人情報保護法で認められた場合を除き、第三者提供は行いません。</li>
                    <li>当社は、一部外国の第三者へ個人情報を提供しております。具体的には、当社で提供するクラウドサービスは、Google社のGoogle Cloud Platformを利用しております。ご提供いただいた個人情報等は、同プラットフォーム上へ管理されます。Google のサーバーは世界各地に設置されており、お客様の情報は、お客様がお住まいの国以外の場所にあるサーバーで処理されることもあります。データ保護法令は国によって異なり、保護の厳格な国もあればそうでない国もあります。Google では、ユーザーの情報が処理される場所にかかわらず、プライバシー ポリシーに定める共通の保護対策を適用しています。そのほか諸外国の個人情報保護に関する制度については、個人情報保護委員会の諸外国・地域の法制度ページ（<a href="https://www.ppc.go.jp/enforcement/infoprovision/laws/" target="_blank" rel="noopener noreferrer">https://www.ppc.go.jp/enforcement/infoprovision/laws/</a>）をご覧ください。</li>
                    <li>同社における個人情報保護の措置として、同社のプライバシーポリシー（<a href="https://policies.google.com/privacy?hl=ja" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy?hl=ja</a>）に記載がある通り、お客様ご自身が正式な書面による異議の申し立てを受領した場合、申し立てを行った方に対し Google から連絡いたします。申し立てを行った方のデータの転送に関する異議について申立人との間で直接解決できなかった場合は、現地のデータ保護当局などの該当する規制当局と協力して解決にあたります。また、同サービスには、個人情報にアクセスできるユーザーを適切に制御するための権限管理機能、ログ管理機能を有していることが確認できており、お客様の個人情報を適切に管理するに足る機能・体制を有していることが確認できております。当社としては安心してお客様にご利用いただけるサービスとして選定の上、利用しております。</li>
                    <li>当社は、個人情報の漏えい、滅失又はき損の防止及び是正のための措置を講じます。</li>
                    <li>当社は、個人情報の取扱いに関する苦情及び相談への適切かつ迅速な対応に努めます。また、当社が保有する保有個人データの開示等の請求等（利用目的の通知、開示、訂正・追加又は削除、利用又は提供の停止）を受け付けます。開示等の請求等の手続きにつきましては、以下の「個人情報苦情及び相談窓口」までご連絡ください。</li>
                    <li>当社は、個人情報保護マネジメントシステムの継続的改善を行います。</li>
                    <li>当社の組織名称・住所・代表者は、以下の通りです。<br/>名称：はてなベース株式会社<br/>住所：東京都新宿区愛住町23 Woody21ビル 6階<br/>代表者：代表取締役 世戸口逸人</li>
                    <li>当社の個人情報保護管理者は、以下の通りです。<br/>世戸口逸人</li>
                    <li>当社の個人情報の取扱いに関する苦情や相談窓口は、以下の通りです。<br/>個人情報保護方針、個人情報苦情及び相談窓口<br/><a href="mailto:seto@hatenabase.com">seto@hatenabase.com</a></li>
                  </ol>
                  <p className="hb-contact-privacy-panel__date">2024年2月1日制定 / 2024年7月20日改定</p>
                </div>
              </section>
            </div>

            {/* ===== Aside ===== */}
            <aside>
              <div style={{padding:'32px', background:hbcp.paper, border:`1px solid ${hbcp.line}`, marginBottom:24}}>
                <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.32em', color:hbcp.blue, marginBottom:14}}>● BY MAIL / PHONE</div>
                <h3 style={{fontFamily:fontJP, fontWeight:700, fontSize:18, margin:'0 0 14px'}}>メール・お電話</h3>
                <div style={{fontFamily:fontMono, fontSize:13, color:hbcp.inkSoft, lineHeight:2}}>
                  MAIL  info@hatenabase.com<br/>
                  TEL   03-4500-2647<br/>
                  受付  平日 9:00 - 18:00
                </div>
              </div>

              <div style={{padding:'32px', background:'#fff', border:`1px solid ${hbcp.line}`, marginBottom:24}}>
                <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.32em', color:hbcp.blue, marginBottom:14}}>● ADDRESS</div>
                <h3 style={{fontFamily:fontJP, fontWeight:700, fontSize:18, margin:'0 0 14px'}}>本社所在地</h3>
                <div style={{fontFamily:fontMono, fontSize:13, color:hbcp.inkSoft, lineHeight:2}}>
                  〒160-0005<br/>
                  東京都新宿区愛住町23-1<br/>
                  woody21 6階
                </div>
              </div>

              <div style={{padding:'32px', background:hbcp.blueGhost, border:`1px solid ${hbcp.line}`}}>
                <div style={{fontFamily:fontENSans, fontSize:10, letterSpacing:'.32em', color:hbcp.blue, marginBottom:14}}>● FAQ</div>
                <h3 style={{fontFamily:fontJP, fontWeight:700, fontSize:18, lineHeight:1.55, margin:'0 0 16px'}}>よくあるご質問</h3>
                <ul style={{margin:0, padding:0, listStyle:'none', display:'grid', gap:14, fontSize:13.5, lineHeight:1.7, color:hbcp.inkSoft}}>
                  <li><strong style={{color:hbcp.ink, fontWeight:600}}>Q. 初回相談は無料ですか？</strong><br/>A. はい、30 分の初回相談は無料です。</li>
                  <li><strong style={{color:hbcp.ink, fontWeight:600}}>Q. 業界の指定はありますか？</strong><br/>A. ありません。SaaS / 士業 / メーカー / 派遣 / 広告など、幅広く対応しています。</li>
                  <li><strong style={{color:hbcp.ink, fontWeight:600}}>Q. 短期 / 単発の依頼も可能ですか？</strong><br/>A. 可能です。スポットでのシステム構築・AX 支援・会計コンサルティングなど、柔軟に対応します。</li>
                </ul>
              </div>
            </aside>
          </div>
        </section>

        {/* CF7 の見た目を Variant D の白×青に揃えるベースライン CSS */}
        <style>{`
          /* モバイル: 横スクロール抑制 + フォームレイアウトを1カラム化 */
          @media (max-width: 860px) {
            .hb-contact-section { padding: 32px 16px 64px !important; }
            .hb-contact-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
            .hb-cf7-host { max-width: 100%; overflow-x: hidden; }
            .hb-cf7-host article,
            .hb-cf7-host .contact-form__item,
            .hb-cf7-host .contact-form__item--agreement { max-width: 100%; box-sizing: border-box; }
            .hb-cf7-host input,
            .hb-cf7-host textarea,
            .hb-cf7-host select { max-width: 100%; box-sizing: border-box; }
            .hb-cf7-host .contact__privacy { max-width: 100%; overflow-x: auto; }
            .hb-cf7-host .contact__privacy p { word-break: break-word; }
            .hb-cf7-host .contact-form__submit { width: 100%; min-width: 0 !important; }
          }
          /* CF7 hidden-fields-container は <fieldset> 要素なので、ブラウザ既定の枠線・余白で
             空の細長いボックスとしてフォーム上端に表示されてしまっていた。完全に消す。 */
          .hb-cf7-host fieldset.hidden-fields-container { all: unset !important; display: contents !important; }
          .hb-cf7-host .wpcf7-form p { margin: 0 0 18px; }
          .hb-cf7-host label { display:grid; gap:6px; font-family:"Zen Kaku Gothic New", sans-serif; font-size:13; font-weight:600; color:${hbcp.ink}; }
          .hb-cf7-host input[type="text"],
          .hb-cf7-host input[type="email"],
          .hb-cf7-host input[type="tel"],
          .hb-cf7-host input[type="url"],
          .hb-cf7-host textarea,
          .hb-cf7-host select {
            width:100%; padding:12px 14px; font-size:14px;
            font-family:"Zen Kaku Gothic New", sans-serif; color:${hbcp.ink};
            background:#fff; border:1px solid ${hbcp.line}; outline:none;
            border-radius:0; box-sizing:border-box;
          }
          .hb-cf7-host textarea { resize:vertical; line-height:1.7; }
          .hb-cf7-host input[type="submit"] {
            padding:16px 36px; background:${hbcp.blue}; color:#fff;
            border:none; cursor:pointer; font-family:"Zen Kaku Gothic New", sans-serif;
            font-size:15px; font-weight:700; letter-spacing:.04em;
            border-radius:0;
          }
          .hb-cf7-host input[type="submit"]:hover { opacity:.92; }
          .hb-cf7-host .wpcf7-list-item { display:inline-block; margin: 0 18px 0 0; }
          .hb-cf7-host .wpcf7-acceptance label { display:inline-flex; gap:8px; font-weight:500; }
          .hb-cf7-host .wpcf7-not-valid-tip { color:${hbcp.blue}; font-size:12px; margin-top:6px; }
          /* レスポンス領域は中身がある時だけ表示 (空でも border-left の青棒が見えてしまうのを回避) */
          .hb-cf7-host .wpcf7-response-output { margin-top:24px; padding:14px 18px; border-left:3px solid ${hbcp.blue}; background:${hbcp.blueGhost}; font-size:13px; }
          .hb-cf7-host .wpcf7-response-output:empty { display:none; }
          .hb-cf7-host span.required, .hb-cf7-host span.wpcf7-required {
            display:inline-block; margin-left:8px; padding:2px 6px;
            font-size:9.5px; letter-spacing:.16em; font-weight:600;
            background:${hbcp.blue}; color:#fff; vertical-align:middle;
          }
          /* legacy CF7 form の送信ボタンは
               <div class="contact-form__submit">
                 送信する  ← プレーンテキスト
                 <button class="view-more-link-simple contact-form__button"></button>  ← 空のクリック領域
               </div>
             という構造。div を見た目のボタンにし、内部 button を absolute で覆いかぶせて
             クリック領域とする (テキストはラッパーから視覚化、押下イベントは button が拾う)。 */
          .hb-cf7-host .contact-form__submit {
            position: relative;
            display: inline-flex; align-items: center; justify-content: center; gap: 10px;
            min-width: 280px; padding: 18px 40px;
            margin: 32px 0 0;
            background: ${hbcp.blue}; color: #fff;
            font-family: "Zen Kaku Gothic New", sans-serif;
            font-size: 15px; font-weight: 700; letter-spacing: .04em;
            cursor: pointer;
            transition: opacity .18s ease;
          }
          .hb-cf7-host .contact-form__submit:hover { opacity: .92; }
          /* 内部の button はテキストを持たない透明オーバーレイにしてクリック専用に */
          .hb-cf7-host .contact-form__submit button,
          .hb-cf7-host .contact-form__button {
            position: absolute; inset: 0;
            width: 100%; height: 100%;
            background: transparent; border: none;
            cursor: pointer; font-size: 0; color: transparent;
          }
          .hb-cf7-host .contact-form__submit .ajax-loader { position: relative; z-index: 1; }

          /* プライバシー同意 (.contact-form__item--agreement) を見出し + checkbox + label 横並びで整形 */
          .hb-cf7-host .contact-form__item--agreement { margin: 28px 0 8px; padding: 18px 20px; background: ${hbcp.paper}; border: 1px solid ${hbcp.line}; }
          .hb-cf7-host .contact-form__item--agreement p { margin: 0 0 8px; font-size: 13.5px; color: ${hbcp.ink}; }
          .hb-cf7-host .contact-form__label--checkbox { display: inline-flex !important; align-items: center; gap: 10px; margin: 0; cursor: pointer; }
          .hb-cf7-host input[type="checkbox"] { width: 18px; height: 18px; cursor: pointer; accent-color: ${hbcp.blue}; }

          /* CF7 一般フォームに埋め込まれている .contact__privacy は重複構造かつ
             他フォームには存在しないため、JSX側で再レンダリングして全フォーム共通化する。
             CF7 のインライン版とその「個人情報保護方針」ラベル行は非表示にする。 */
          .hb-cf7-host .contact__privacy,
          .hb-cf7-host .contact-form__item--privacy,
          .hb-cf7-host .contact-form__label--privacy { display: none !important; }

          /* JSX側で全フォーム共通レンダリングする個人情報保護方針パネル
             デザイン: NYT 風 editorial — 罫線・大見出し・番号付きOL・スクロール */
          .hb-contact-privacy-panel {
            margin: 28px 0 4px;
            background: #fff;
            border: 1px solid ${hbcp.line};
            border-radius: 8px;
            overflow: hidden;
          }
          .hb-contact-privacy-panel__header {
            padding: 22px 28px 18px;
            border-bottom: 1px solid ${hbcp.line};
            background: ${hbcp.paper};
          }
          .hb-contact-privacy-panel__kicker {
            font-family: ${fontENSans};
            font-size: 10px;
            letter-spacing: .32em;
            color: ${hbcp.blue};
            font-weight: 600;
            margin-bottom: 8px;
          }
          .hb-contact-privacy-panel__title {
            font-family: ${fontJP};
            font-size: 17px;
            font-weight: 700;
            letter-spacing: .02em;
            color: ${hbcp.ink};
            margin: 0;
            line-height: 1.4;
          }
          .hb-contact-privacy-panel__body {
            position: relative;
            padding: 22px 28px 24px;
            max-height: 360px;
            overflow-y: auto;
            font-size: 12.5px;
            line-height: 1.95;
            color: ${hbcp.inkSoft};
            letter-spacing: .01em;
            box-shadow: inset 0 -18px 22px -22px rgba(0,0,0,.14);
            scrollbar-width: thin;
            scrollbar-color: ${hbcp.line} transparent;
          }
          .hb-contact-privacy-panel__body::-webkit-scrollbar { width: 8px; }
          .hb-contact-privacy-panel__body::-webkit-scrollbar-thumb { background: ${hbcp.line}; border-radius: 4px; }
          .hb-contact-privacy-panel__body::-webkit-scrollbar-track { background: transparent; }
          .hb-contact-privacy-panel__body p { margin: 0 0 14px; }
          .hb-contact-privacy-panel__body ol {
            margin: 12px 0 16px;
            padding-left: 22px;
            counter-reset: hb-priv-num;
            list-style: none;
          }
          .hb-contact-privacy-panel__body ol > li {
            position: relative;
            margin: 0 0 10px;
            padding-left: 6px;
            counter-increment: hb-priv-num;
          }
          .hb-contact-privacy-panel__body ol > li::before {
            content: counter(hb-priv-num) ".";
            position: absolute;
            left: -22px;
            top: 0;
            width: 22px;
            font-family: ${fontENSans};
            font-size: 11px;
            font-weight: 700;
            color: ${hbcp.blue};
            text-align: right;
            padding-right: 6px;
            box-sizing: border-box;
            letter-spacing: .04em;
          }
          .hb-contact-privacy-panel__body a {
            color: ${hbcp.blue};
            text-decoration: underline;
            text-underline-offset: 2px;
            word-break: break-all;
          }
          .hb-contact-privacy-panel__date {
            margin-top: 18px !important;
            padding-top: 14px;
            border-top: 1px solid ${hbcp.line};
            font-family: ${fontMono};
            font-size: 11px !important;
            color: ${hbcp.inkLight};
            letter-spacing: .04em;
          }
          @media (max-width: 860px) {
            .hb-contact-privacy-panel__header { padding: 18px 18px 14px; }
            .hb-contact-privacy-panel__title { font-size: 15px; }
            .hb-contact-privacy-panel__body { padding: 18px 18px 20px; max-height: 320px; font-size: 12px; }
            .hb-contact-privacy-panel__body ol { padding-left: 20px; }
          }
        `}</style>
      </HBLayout>
    );
  }

  Object.assign( window, { ContactPage } );
})();
